import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { listContainersWithFields } from "@/lib/http/resources"
import type { ResourceField } from "@/types/api"

export type MentionItemKind = "table" | "column"

export interface MentionItem {
  label: string
  insert: string
  kind: MentionItemKind
}

export function useMentionOptions(resourceId: string | null): MentionItem[] {
  const { data: containersWithFields = [] } = useQuery({
    queryKey: ["resources", "containers-with-fields", resourceId] as const,
    queryFn: () => listContainersWithFields(resourceId!),
    enabled: !!resourceId,
  })

  return useMemo(() => {
    if (!resourceId) return []

    const items: MentionItem[] = []
    const getContainerNamespace = (container: { meta?: { namespace?: string; schema?: string } }) => {
      const namespace = container.meta?.namespace || container.meta?.schema
      if (!namespace || !namespace.trim()) {
        return "public"
      }
      return namespace
    }

    // Tables (containers)
    containersWithFields.forEach((c) => {
      const namespace = getContainerNamespace(c)
      const qualifiedTable = `${namespace}.${c.name}`
      items.push({
        label: qualifiedTable,
        insert: `@${qualifiedTable}`,
        kind: "table",
      })
    })

    // Columns (namespace.table.column) — API uses field_name / field_path
    containersWithFields.forEach((c) => {
      const namespace = getContainerNamespace(c)
      const qualifiedTable = `${namespace}.${c.name}`
      const fields = (c.fields ?? []) as ResourceField[]
      fields.forEach((f) => {
        const colName = f.field_name ?? f.field_path ?? ""
        if (!colName) return
        items.push({
          label: `${qualifiedTable}.${colName}`,
          insert: `@${qualifiedTable}.${colName}`,
          kind: "column",
        })
      })
    })

    return items
  }, [resourceId, containersWithFields])
}
